import { useEffect, useState } from 'react';

import { Badge, Button, Space } from '@sravni/react-design-system';
import { useBoolean, useStyles } from '@sravni/react-utils';

import { SravniQuizModelsFiltersOption } from '../../__generated__/quiz/Api';
import { OptionsWithPercents } from '../../server/services/quiz';

import styles from './survey-widget.module.scss';

interface SurveyProps {
  onSubmit: (optionIds: SravniQuizModelsFiltersOption[]) => Promise<void>;

  theme: string;
  isSurveyCompleted: boolean;
  title: string;
  comment: string;
  options: OptionsWithPercents;
  isMultipleAnswers: boolean;
}

export const Survey = ({
  theme,
  isSurveyCompleted,
  title,
  comment,
  onSubmit,
  options,
  isMultipleAnswers,
}: SurveyProps) => {
  const cx = useStyles(styles);
  const [questionValues, setQuestionValues] = useState<{ [key: string]: boolean }>({});
  const [multipleQuestionValues, setMultipleQuestionValues] = useState<{ [key: string]: boolean }>({});
  const [isVoteSending, setVoteSending] = useBoolean(false);

  const handleSubmit = async (event) => {
    event?.preventDefault();

    const optionsId = Object.entries(isMultipleAnswers ? multipleQuestionValues : questionValues)
      .filter(([_, value]) => value)
      .map(([optionId]) => new Object({ optionId }));

    if (!optionsId.length) return;
    setVoteSending.on();

    await onSubmit(optionsId);

    setVoteSending.off();
  };

  useEffect(() => {
    if (isSurveyCompleted) return;

    handleSubmit(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSurveyCompleted, questionValues]);

  return (
    <div className={cx('survey', `survey--${theme}`, { 'survey--closed': isSurveyCompleted })}>
      <Badge text={'Опрос'} color={'gray'} variant={'primary'} />
      <h1 className={cx('article-h1', 'h-mt-12')}>{title}</h1>
      <p className={cx('lead', 'article-description', 'h-mt-12')}>{comment}</p>
      <form onSubmit={handleSubmit} className={cx('form', 'h-mt-12')}>
        {Object.values(options).map((option) => {
          const { id, text: optionText, percent, icon } = option;

          const handleOptionChange = () => {
            setQuestionValues((values) => ({
              ...values,
              [id]: !values['id'] ?? true,
            }));
          };

          const handleOptionChangeMultiple = () => {
            setMultipleQuestionValues((values) => ({
              ...values,
              [id]: !values['id'] ?? true,
            }));
          };

          // @ts-ignore
          const styles = { style: { '--percent': `${percent}%` } };

          return (
            <div key={`option-${id}`} className={cx('option')}>
              <input
                type={isMultipleAnswers ? 'checkbox' : 'radio'}
                id={id}
                value={id}
                name={'question1'}
                className={cx('input')}
                onChange={isMultipleAnswers ? handleOptionChangeMultiple : handleOptionChange}
              />
              {/*@ts-ignore*/}
              <label htmlFor={id} className={cx('article-description', 'label')} {...(isSurveyCompleted && styles)}>
                <Space direction={'horizontal'}>
                  <span className={cx('label__icon', 'h-mr-8')}>{icon}</span>
                  <span className={cx('label__text', 'h-color-D100')}>{optionText}</span>
                  {isSurveyCompleted && <span className={cx('label__percent', 'h-ml-8')}>{percent}%</span>}
                </Space>
              </label>
            </div>
          );
        })}
        {isMultipleAnswers && !isSurveyCompleted && (
          <Button
            variant={'primary'}
            color={'gray'}
            block={false}
            className={cx('button', 'h-mt-12')}
            loading={isVoteSending}
            disabled={!Object.values(questionValues)}
            name={'Проголосовать'}
          >
            Проголосовать
          </Button>
        )}
      </form>
    </div>
  );
};
